import Backbone from 'backbone';
import _ from 'underscore';
import Handlebars from 'handlebars-template-loader/runtime';

import formatCssClass from 'helpers/formatCssClass';
import getBrowser from 'helpers/getBrowser';
import assertValidConfig from 'helpers/assertValidConfig';

export default Backbone.View.extend(
  /**
   * @lends core.components.base.ComponentBase.prototype
   */
  {

    /**
     * The CSS class used for the root element of the component. It needs to be set in derivate classes.
     * @protected
     * @readonly
     * @type {string}
     */
    componentClass: null,

    /**
     * The widget instance the component is created within.
     * @protected
     * @readonly
     * @type {core.widget.WidgetCommon}
     */
    widget: null,

    /**
     * The event bus instance used by extending component classes to talk to other components
     * @protected
     * @readonly
     * @type {core.widget.widgetHelpers.EventBus}
     */
    bus: null,

    /** 
     * Validation function, normally generated by requiring an JSON schema file and 
     * letting Webpack compile the proper validaiton function.
     * Override this to allow for adding custom configuration properties to the class extending ComponentBase.
     * @function
     * @param {core.component.base.ComponentBase.ComponentBaseConfig} config
     * @protected
     */
    validation: require('./componentBase.schema.json'),

    /**
     * Events to be attached to the HTML of the component. Keys must be of the form "eventName classSelector", 
     * and values the name of the method to call.
     * @protected
     * @type {Object<string,string>}
     */
    events: {
      'click': 'toggleActivation',
      'click .close-btn': '_closefn'
    },

    /** 
     * It is a template function used to generate the component's DOM, normally generated by requiring a Handlebars template and 
     * letting the appropiate Webpack loader convert it to a function.
     *
     * ComponentBase extending classes must provide their own templates.
     * @protected
     * @function 
     * @param {object} data The data to be used to be referenced in the template to generate the DOM.
     */
    template: null,

    /**
     * The data to be passed to the template function  when it gets rendered. 
     * @protected
     */
    templateConfig: {},

    /**
     * @typedef {object} ComponentBaseConfig
     * @property {core.widget.Widget} widget The widget instance the component is attached to.
     * @property {string} identifier The identifier of the component.
     * @property {string} [position=] The position of the component in the map or parent component
     * @property {object} [properties={}] The configuration for the component 
     * @property {string[]} [imcompatibilities] Identifiers of components this component is incompatible with,
     * i.e. if this component is active and one on this array is activated, this component will deactivate.
     * @property {boolean} [isChild=false] True if the component is child of another component
     * @memberof core.components.base.ComponentBase
     */

    /**
     * Creates a ComponentBase instance.
     * @class ComponentBase
     * @memberof core.components.base
     * @classdesc This is a component base on which the visualization components will be built
     * @extends Backbone.View
     * @abstract
     * @param {core.components.base.ComponentBase.ComponentBaseConfig} options The config provided by json.    
     * @throws {Error} If the options parameter doesn't contain widget, componentClass or identifier properties.
     */
    constructor: function(options) {
      if (!options.properties) {
        options.properties = {};
      }

      this.position = options.position;
      this.widget = options.widget;

      if (!this.widget) {
        throw new Error('ComponentBase::constructor: needs a widget property in the options');
      }

      if (!options.identifier) {
        throw new Error('ComponentBase::constructor: the component identifier must be received in the options');
      }
      this.identifier = options.identifier;

      if (!this.componentClass) {
        throw new Error(
          'ComponentBase::constructor: A ComponentBase subclass needs to define a componentClass property');
      }

      assertValidConfig(`Component ${this.identifier}`, this.validation, options);

      this.map = options.map || options.widget.map;
      this.imagesPath = this.widget.config.imagesPath;
      this.isChild = options.isChild || false;

      this.bus = options.widget.bus;

      this.incompatibilities = options.incompatibilities || [];
      this.componentConstructorClass = options.componentConstructorClass;

      this.templateConfig = {
        componentClass: this.componentClass || null,
        iconClassName: options.properties ? options.properties.iconClassName : null
      };
      this.isActivated = false;
      this.filterManager = options.widget.filterManager || null;
      this.handleLayers = this.widget.handleLayers;
      this.properties = options.properties || {};
      this.parentComponent = options.parent;

      Backbone.View.prototype.constructor.call(this, options);
    },

    /**
     * Initialize the component and insert it as either child of another component,
     * embedded in the map or as Google Maps Control
     * @summary Initialize the component
     * @function
     * @memberof ComponentBase
     * @param {Object} options - The config provided by json
     * @listens  Widget#ACTIVATE_TOOL
     */
    initialize: function(options) {

      if (_.isUndefined(this.template)) {
        return;
      }

      if (!_.isUndefined(this.templatePartials)) {
        for (let partialName in this.templatePartials) {
          if (!this.templatePartials.hasOwnProperty(partialName)) {
            continue;
          }
          Handlebars.default.registerPartial(partialName, this.templatePartials[partialName]);
        }
      }

      //render the template
      if (!this.position && _.isUndefined(this.widget.map)) { //Autocomplete widget
        this.widget.$el.find('.lmm-autocomplete-widget-container').append(this.render().el);
      } else if (this.isChild) {
        this.render();
      } else if (!this.position) {
        this.widget.$el.find('.lmm-maps-widget-container').append(this.render().el);
      } else {
        this.map.controls[google.maps.ControlPosition[this.position]].push(this.render().el);
      }

      if (!_.isEmpty(this.incompatibilities)) {
        this.listenTo(this.bus, 'ACTIVATE_TOOL', (tool) => {
          if (this.isActivated && _.contains(this.incompatibilities, tool.identifier)) {
            this.deactivate({
              disable: true
            });
          }
        });
      }
    },

    /**
     * Changes the component's activation state
     */
    toggleActivation() {
      if (!this.isActivated) {
        this.activate();
      } else {
        this.deactivate();
      }
    },

    /**
     * Activate the component
     * @fires Widget#ACTIVATE_TOOL
     */
    activate() {
      this.isActivated = true;
      /**
       * Activate the component.
       * @event Widget#ACTIVATE_TOOL
       * @type {object}
       * @property {object} myObj
       * @property  {string} myObj.identifier - Indicates the id of the hovered marker
       * @property  {ComponentBase} myObj.instanceObj - The instance of component
       */
      this.bus.trigger('ACTIVATE_TOOL', {
        identifier: this.identifier,
        instanceObj: this
      });
    },

    /**
     * Deactivate the component
     * @params {object} options - The options that will send in event
     * @fires Widget#DEACTIVATE_TOOL
     */
    deactivate(options) {
      let deactivationOpt = _.extend({
        identifier: this.identifier
      }, options);
      this.isActivated = false;
      /**
       * Deactivate the component.
       * @event Widget#DEACTIVATE_TOOL
       * @type {object}
       * @property {object} deactivationOpt - The options for deactivation
       * @property  {string} deactivationOpt.identifier - Indicates the id of the hovered marker
       */
      this.bus.trigger('DEACTIVATE_TOOL', deactivationOpt);
    },

    /**
     * Hide the view of the component. Put display as 'none'
     * @function
     */
    hide() {
      let name = getBrowser().name;
      if (name === 'IE' || name === 'Edge') {
        this.$el.wrap('<span>').hide();
      }
      if (this.position && google.maps.ControlPosition[this.position]) {
        let idx = this.map.controls[google.maps.ControlPosition[this.position]].indexOf(this.el);
        if (idx !== -1) {
          this.map.controls[google.maps.ControlPosition[this.position]].removeAt(idx);
        }
      }
      this.$el.hide();
    },

    /**
     * Show the view of the component. Put display as 'block'
     * @function
     */
    show() {
      this.$el.show();
      // position is a map position (not a container's) 
      // and it isn't already set as control to avoid duplicates
      if (this.position &&
        this.map.controls[google.maps.ControlPosition[this.position]] &&
        this.map.controls[google.maps.ControlPosition[this.position]].indexOf(this.el) === -1) {
        this.map.controls[google.maps.ControlPosition[this.position]].push(this.el);
      }
    },

    /**
     * Overrides 
     * @final 
     * @override
     */
    delegateEvents() {
      this.customEventDelegation();
      Backbone.View.prototype.delegateEvents.call(this);
    },

    /**
     * Allows to add a specified functionality before ComponentBase#delegateEvents
     * @function
     * @protected
     * @virtual
     */
    customEventDelegation() {},

    /**
     * Renders the template of the widget
     * @function
     * @returns {ComponentBase} the instance
     */
    render: function() {
      let classComp = (this.isChild) ? 'component' : 'componentOnMap';
      this.setElement(this.template(this.templateConfig));
      this.$el
        .addClass(formatCssClass(this.componentClass))
        .addClass(formatCssClass(classComp))
        .addClass(formatCssClass(this.identifier))
        .addClass(formatCssClass(`${this.componentClass}-${this.identifier}`));

      return this;
    },

    _closefn() {
      if (this.parentComponent) {
        this.parentComponent.unSelect();
      }
    }

  });



// WEBPACK FOOTER //
// ./lmm-widget/src/components/_base/ComponentBase.js